<script setup>
import DialogDrawer from '@js/components/dialogs/DialogDrawer.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { reactive, ref } from 'vue';
import { object, string } from 'yup';

const { server, callback } = defineProps({
    server: {
        type: Object,
        required: true,
    },
    callback: {
        type: Function,
        required: false,
    },
});

const { route } = useRoute();

const isSubmitting = ref(false);

const isOpen = ref(false);

const form = reactive({
    name: server.name,
    serverName: server.serverName,
    commitment: server.commitment,
    renewalDate: server.renewalDate,
    costExcludedTaxPerMonth: server.costExcludedTaxPerMonth,
    ipBlockCostExcludedTaxPerMonth: server.ipBlockCostExcludedTaxPerMonth,
    maintenanceCostExcludedTaxPerMonth:
        server.maintenanceCostExcludedTaxPerMonth,
    ips: server.ips,
    phpVersions: server.phpVersions,
    ipBlock: server.ipBlock,
    backupHost: server.backupHost,
    backupLogin: server.backupLogin,
    backupPassword: server.backupPassword,
    backupStorageSpace: server.backupStorageSpace,
    additionalInformation: server.additionalInformation,
    isSshLimitedByIp: server.isSshLimitedByIp,
    isSshProtectedByKey: server.isSshProtectedByKey,
    isServerAdminLimitedByIp: server.isServerAdminLimitedByIp,
});

const validationSchema = object({
    name: string().required('Ce champ est obligatoire.'),
    serverName: string().nullable(),
    commitment: string().nullable(),
    renewalDate: string().nullable(),
    costExcludedTaxPerMonth: string().nullable(),
    ipBlockCostExcludedTaxPerMonth: string().nullable(),
    maintenanceCostExcludedTaxPerMonth: string().nullable(),
    ips: string().nullable(),
    phpVersions: string().nullable(),
    ipBlock: string().nullable(),
    backupHost: string().nullable(),
    backupLogin: string().nullable(),
    backupPassword: string().nullable(),
    backupStorageSpace: string().nullable(),
    additionalInformation: string().nullable(),
    isSshLimitedByIp: string().nullable(),
    isSshProtectedByKey: string().nullable(),
    isServerAdminLimitedByIp: string().nullable(),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.dev.postServer', {
                server: server.id,
            }),
            {
                name: form.name,
                serverName: form.serverName,
                commitment: form.commitment,
                renewalDate: form.renewalDate,
                costExcludedTaxPerMonth: form.costExcludedTaxPerMonth,
                ipBlockCostExcludedTaxPerMonth:
                    form.ipBlockCostExcludedTaxPerMonth,
                maintenanceCostExcludedTaxPerMonth:
                    form.maintenanceCostExcludedTaxPerMonth,
                ips: form.ips,
                phpVersions: form.phpVersions,
                ipBlock: form.ipBlock,
                backupHost: form.backupHost,
                backupLogin: form.backupLogin,
                backupPassword: form.backupPassword,
                backupStorageSpace: form.backupStorageSpace,
                additionalInformation: form.additionalInformation,
                isSshLimitedByIp: form.isSshLimitedByIp,
                isSshProtectedByKey: form.isSshProtectedByKey,
                isServerAdminLimitedByIp: form.isServerAdminLimitedByIp,
            },
        )
        .then(() => {
            isOpen.value = false;

            if (callback) {
                callback();
            }
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            isSubmitting.value = false;
        });
}
</script>

<template>
    <DialogDrawer v-model="isOpen" size="extra-large">
        <template #trigger>
            <slot name="trigger" />
        </template>

        <template #title> Mettre à jour le serveur {{ server.name }} </template>

        <template #full-content>
            <FormRoot
                :validation-schema="validationSchema"
                :submit="submit"
                v-slot="{ errors }"
            >
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="1">
                            <InputField
                                v-model="form.name"
                                label="Nom"
                                placeholder="Nom"
                                field-name="name"
                                field-id="name"
                                required
                            />

                            <InputField
                                v-model="form.serverName"
                                label="Nom du serveur"
                                placeholder="Nom du serveur"
                                field-name="serverName"
                                field-id="serverName"
                            />

                            <InputField
                                v-model="form.commitment"
                                label="Engagement"
                                placeholder="Engagement"
                                field-name="commitment"
                                field-id="commitment"
                                :class="{ hidden: server.id === 8 }"
                            />

                            <InputField
                                v-model="form.renewalDate"
                                label="Date de renouvellement"
                                field-name="renewalDate"
                                field-id="renewalDate"
                                :class="{ hidden: server.id === 8 }"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent :class="{ hidden: server.id === 8 }">
                        <FormGrid :columns="2">
                            <InputField
                                v-model="form.costExcludedTaxPerMonth"
                                label="Coût HT/mois"
                                placeholder="Coût HT/mois"
                                field-name="costExcludedTaxPerMonth"
                                field-id="costExcludedTaxPerMonth"
                                type="number"
                            />

                            <InputField
                                v-model="form.ipBlockCostExcludedTaxPerMonth"
                                label="Coût bloc IP HT/mois"
                                placeholder="Coût bloc IP HT/mois"
                                field-name="ipBlockCostExcludedTaxPerMonth"
                                field-id="ipBlockCostExcludedTaxPerMonth"
                                type="number"
                            />

                            <InputField
                                v-model="
                                    form.maintenanceCostExcludedTaxPerMonth
                                "
                                label="Coût maintenance HT/mois"
                                placeholder="Coût maintenance HT/mois"
                                field-name="maintenanceCostExcludedTaxPerMonth"
                                field-id="maintenanceCostExcludedTaxPerMonth"
                                type="number"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent :class="{ hidden: server.id === 8 }">
                        <FormGrid :columns="1">
                            <InputField
                                v-model="form.ips"
                                label="IPs"
                                placeholder="IPs"
                                field-name="ips"
                                field-id="ips"
                            />

                            <EditorField
                                v-model="form.ipBlock"
                                label="Bloc IP"
                                field-name="ipBlock"
                                field-id="ipBlock"
                                size="small"
                            />

                            <InputField
                                v-model="form.phpVersions"
                                label="Versions PHP"
                                placeholder="Versions PHP"
                                field-name="phpVersions"
                                field-id="phpVersions"
                            />

                            <InputField
                                v-model="form.backupHost"
                                label="Hôte backup"
                                placeholder="Hôte backup"
                                field-name="backupHost"
                                field-id="backupHost"
                            />

                            <InputField
                                v-model="form.backupLogin"
                                label="Nom d'utilisateur backup"
                                placeholder="Nom d'utilisateur backup"
                                field-name="backupLogin"
                                field-id="backupLogin"
                            />

                            <InputField
                                v-model="form.backupStorageSpace"
                                label="Espace de stockage backup"
                                placeholder="Espace de stockage backup"
                                field-name="backupStorageSpace"
                                field-id="backupStorageSpace"
                            />

                            <InputField
                                v-model="form.backupPassword"
                                label="Mot de passe backup"
                                placeholder="Mot de passe backup"
                                field-name="backupPassword"
                                field-id="backupPassword"
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="1">
                            <EditorField
                                v-model="form.additionalInformation"
                                label="Informations complémentaires"
                                field-name="additionalInformation"
                                field-id="additionalInformation"
                                size="small"
                            />

                            <SwitchField
                                field-name="isSshLimitedByIp"
                                field-id="isSshLimitedByIp"
                                label="SSH limité par IP"
                                v-model="form.isSshLimitedByIp"
                            />

                            <SwitchField
                                field-name="isSshProtectedByKey"
                                field-id="isSshProtectedByKey"
                                label="SSH protégé par clé"
                                v-model="form.isSshProtectedByKey"
                            />

                            <SwitchField
                                field-name="isServerAdminLimitedByIp"
                                field-id="isServerAdminLimitedByIp"
                                label="Virtualmin limité par IP"
                                v-model="form.isServerAdminLimitedByIp"
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>

                <FormFooter
                    :is-submitting="isSubmitting"
                    :errors="errors"
                    without-back-button
                />
            </FormRoot>
        </template>
    </DialogDrawer>
</template>
